import React, { useState } from "react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { Payment } from "../../features/payments/models/Payment";
import { PaymentStatus } from "../../features/payments/models/PaymentStatus";
import { PaymentStatusChange } from "../../features/payments/models/PaymentStatusChange";
import { updatePaymentDocumentNote } from "../../features/payments/slice-extensions/payments-slice-extension";

interface PaymentDetailRowProps {
  payment: Payment;
  history: PaymentStatusChange[];
  colSpan: number;
}

/** The expanded detail row under a payment: created date, pack size, notes, document note, audit log. */
const PaymentDetailRow: React.FC<PaymentDetailRowProps> = ({ payment, history, colSpan }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [documentNote, setDocumentNote] = useState(payment.documentNote ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const noteDirty = documentNote.trim() !== (payment.documentNote ?? "");
  const canHaveDocument =
    payment.status !== PaymentStatus.Draft && payment.status !== PaymentStatus.Voided;

  const handleSaveNote = async () => {
    if (!noteDirty || saving) return;
    setSaving(true);
    setSaved(false);
    const result = await dispatch(
      updatePaymentDocumentNote({ id: payment.id, documentNote: documentNote.trim() || null })
    );
    setSaving(false);
    if (updatePaymentDocumentNote.fulfilled.match(result)) {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
  };

  return (
    <tr className="border-t border-grey-90 bg-grey-95 dark:border-primary-20 dark:bg-primary-20">
      <td className="px-4 py-3" colSpan={colSpan}>
        <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
          <p className="text-grey-30 dark:text-grey-70">
            {t("billing.detail.created")}: <span className="text-grey-10 dark:text-grey-90">{payment.dateCreated.slice(0, 10)}</span>
          </p>
          {payment.invoiceNumber != null && (
            <p className="text-grey-30 dark:text-grey-70">
              {t("billing.detail.invoiceNumber")}: <span className="text-grey-10 dark:text-grey-90">{payment.invoiceNumber}</span>
            </p>
          )}
          {payment.packSize != null && (
            <p className="text-grey-30 dark:text-grey-70">
              {t("billing.detail.packSize")}: <span className="text-grey-10 dark:text-grey-90">{payment.packSize}</span>
            </p>
          )}
          {payment.notes && (
            <p className="text-grey-30 dark:text-grey-70 sm:col-span-3">
              {t("billing.detail.notes")}: <span className="text-grey-10 dark:text-grey-90">{payment.notes}</span>
            </p>
          )}
        </div>
        {canHaveDocument && (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center" onClick={(e) => e.stopPropagation()}>
            <label className="text-sm text-grey-30 dark:text-grey-70 shrink-0" htmlFor={`document-note-${payment.id}`}>
              {t("billing.detail.documentNote")}:
            </label>
            <input
              id={`document-note-${payment.id}`}
              type="text"
              className="input w-full sm:max-w-md"
              placeholder={t("billing.detail.documentNotePlaceholder")}
              value={documentNote}
              maxLength={500}
              onChange={(e) => setDocumentNote(e.target.value)}
              disabled={saving}
            />
            <button
              type="button"
              className="btn btn-secondary shrink-0"
              onClick={handleSaveNote}
              disabled={!noteDirty || saving}
            >
              {saving ? t("common.saving") : t("common.saveChanges")}
            </button>
            {saved && (
              <span className="flex items-center gap-1 text-sm text-green-700 dark:text-green-400">
                <Check size={16} />
              </span>
            )}
          </div>
        )}
        {history.length > 0 && (
          <ul className="mt-2 space-y-1 text-xs text-grey-30 dark:text-grey-70">
            {history.map((change) => (
              <li key={change.id}>
                {change.changedAt.slice(0, 10)} — {t(`billing.paymentStatus.${change.fromStatus}`, { defaultValue: change.fromStatus })} → {t(`billing.paymentStatus.${change.toStatus}`, { defaultValue: change.toStatus })}
                {change.note ? ` · ${change.note}` : ""}
              </li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
};

export default PaymentDetailRow;
